import { createContext, useCallback, useMemo, useState } from "react";

import { DateTime } from "luxon";

import type { CreateShipmentFormState } from "types/shipment";

export function createInitialFormState(): CreateShipmentFormState {
  return {
    shipmentType: "SINGLE",
    equipmentTypes: [],
    service: "",
    commodity: "",
    weightLb: null,
    footage: "",
    specialRequirements: "",
    additionalDetails: "",
    preferredPrice: "",
    routePoints: [
      {
        clientId: "origin",
        stop_type: "ORIGIN",
        stop_operation: "PICKUP",
        point_number: 1,
        start_time: DateTime.now().toFormat("yyyy-MM-dd"),
        isBeingEdited: true,
        inputMode: "autocomplete",
        schedule_type: "DATETIME",
        point_location: {
          address: "",
          city: "",
          province: "",
          country: "",
          postal: "",
          latitude: null,
          longitude: null,
          business_name: "",
        },
      },
      {
        clientId: "destination",
        stop_type: "DESTINATION",
        stop_operation: "DROPOFF",
        point_number: 2,
        start_time: DateTime.now().toFormat("yyyy-MM-dd"),
        isBeingEdited: true,
        inputMode: "autocomplete",
        schedule_type: "ASAP",
        point_location: {
          address: "",
          city: "",
          province: "",
          country: "",
          postal: "",
          latitude: null,
          longitude: null,
          business_name: "",
        },
      },
    ],
    currency: null,
    notificationSettingsType: "ALL_CARRIER_NETWORK",
    notificationSetingsEmails: [],
    commodityFreetext: "",
    isBookNow: false,
    bookNowPrice: "",
    bookNowCurrency: "CAD",
    customName: "",
    emailNotificationType: "ALL",
    shareeRoleIds: [],
    shareShipment: false,
    availableLoads: null,
    customerRoleIds: [],
    externalCustomerIds: [],
    customerInviteEmails: [],
    customerSnapshots: [],
    partnerGroups: [],
    files: [],
    quotingUnitOfMeasure: "ALL_IN",
    analytics: {
      shipmentCreationMode: "MANUAL",
      creationStartingTime: null,
      encounteredErrors: [],
      routePointAddressModes: { 0: "autocomplete", 1: "autocomplete" },
    },
    networkInviteEmails: [],
    directBookingAssignment: null,
  };
}

interface CreateShipmentFormDraftContextValue {
  formState: CreateShipmentFormState;
  setFormState: React.Dispatch<React.SetStateAction<CreateShipmentFormState>>;
  resetFormState: () => void;
}

export const createShipmentFormDraftContext =
  createContext<CreateShipmentFormDraftContextValue | null>(null);

interface CreateShipmentFormDraftProviderProps {
  children: React.ReactNode;
}

export function CreateShipmentFormDraftProvider(
  props: CreateShipmentFormDraftProviderProps,
) {
  const { children } = props;
  const [formState, setFormState] = useState<CreateShipmentFormState>(
    createInitialFormState,
  );

  const resetFormState = useCallback(() => {
    setFormState(createInitialFormState());
  }, []);

  const value = useMemo(
    () => ({ formState, setFormState, resetFormState }),
    [formState, resetFormState],
  );

  return (
    <createShipmentFormDraftContext.Provider value={value}>
      {children}
    </createShipmentFormDraftContext.Provider>
  );
}
